import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, Col, Flex, Row, Spin, Table, Tag } from 'antd'
import FormItem from 'antd/es/form/FormItem'
import { ColumnsType } from 'antd/es/table'
import Link from 'antd/es/typography/Link'
import { AdGroupBean, CreativeBean, CreativeListItemBean } from 'common/api/api'
import { creativeApi } from 'common/api/entitiesApi'
import { CreativeSelectorFiltered } from 'common/components/reloadable/CreativeSelectorFiltered.tsx'
import { useAppContext } from 'common/context/AppContext'
import { COLUMN_ID_WIDTH } from 'common/util/constants.ts'
import { apiCall, formatNames, FormMode, showError } from 'common/util/Util'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { getCpcCreativeValidationError, mapFormatToGroup } from './creativeGroups'

import StatusEnum = CreativeBean.StatusEnum
import PricingTypeEnum = AdGroupBean.PricingTypeEnum
import FormatEnum = CreativeListItemBean.FormatEnum
import { CommonRoutes } from 'common/context/commonRoutes.ts'

export interface Item {
  id: number
  name: string
  format: string
  status: StatusEnum
  indicator: StatusEnum
  active: string
  stats: any
}

interface Props {
  entity: AdGroupBean
  setEntity: (entity: AdGroupBean) => void
  saveAdGroupState: (extraState?: Record<string, unknown>) => void
  adGroupId: any
  mode: FormMode
  pricingType?: PricingTypeEnum
}

const CreativesCard = ({ entity, setEntity, saveAdGroupState, adGroupId, mode, pricingType }: Props) => {
  const appContext = useAppContext()
  const [loading, setLoading] = useState(false)
  const [listData, setListData] = useState<CreativeListItemBean[]>([])
  const navigateTo = useNavigate()
  const [selectorKey, setSelectorKey] = useState(0)

  useEffect(() => {
    let cancelled = false

    if (entity.creativeIds && entity.creativeIds.length > 0) {
      apiCall(
        setLoading,
        appContext,
        () =>
          creativeApi.creativeList(
            'name',
            'asc',
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            entity.creativeIds,
          ),
        (listItem) => {
          if (cancelled) return
          setListData(listItem.data?.map((item) => item.data || ({} as CreativeListItemBean)) || [])
        },
      )
    } else {
      setListData([])
    }

    return () => {
      cancelled = true
    }
  }, [entity.creativeIds])

  const visibleListData = useMemo(
    () => listData.filter((item) => item.id !== undefined && entity.creativeIds?.includes(item.id)),
    [entity.creativeIds, listData],
  )

  const existingGroup = useMemo(() => {
    for (const c of visibleListData) {
      const g = mapFormatToGroup(c.format)
      if (g) return g
    }
    return null
  }, [visibleListData])

  const handleCreateOrEditCreative = (id?: number) => {
    saveAdGroupState({
      creativeGroupsSnapshot: Object.fromEntries(
        visibleListData
          .map((creative) => [creative.id, mapFormatToGroup(creative.format)])
          .filter((entry): entry is [number, NonNullable<ReturnType<typeof mapFormatToGroup>>] => entry[1] !== null),
      ),
    })
    appContext.setParentAdvertiserId(entity.advertiserId)
    appContext.setBackToAdGroupId(adGroupId)
    appContext.setBackToAdGroupMode(mode)
    navigateTo(id ? `/creative/${id}/edit` : CommonRoutes.CREATIVE_CREATE)
  }

  const unlink = async (id: number) => {
    setEntity({
      ...entity,
      creativeIds: entity.creativeIds && entity.creativeIds.filter((creativeId: number) => creativeId !== id),
    })
    setListData((prev) => prev.filter((item) => item.id !== id))
  }

  const addCreative = async (id?: number) => {
    if (!id) return
    if (entity.creativeIds?.includes(id)) return

    if (pricingType === PricingTypeEnum.CPC) {
      let format: FormatEnum | undefined
      try {
        const res = await creativeApi.getCreative(id)
        format = res?.data?.format as any
      } catch (e) {
        showError(appContext, e)
        return
      }

      const validationError = getCpcCreativeValidationError(format, existingGroup)
      if (validationError) {
        showError(appContext, validationError)
        return
      }
    }

    setEntity({
      ...entity,
      creativeIds: entity.creativeIds ? [...entity.creativeIds, id] : [id],
    })
  }

  const columns: ColumnsType<any> = [
    { title: 'ID', dataIndex: 'id', width: COLUMN_ID_WIDTH },
    {
      title: 'Linked Creative',
      dataIndex: 'name',
      render: (_: any, record: Item) =>
        record.id ? <Link onClick={() => handleCreateOrEditCreative(record.id)}>{record.name}</Link> : null,
    },
    {
      title: 'Review',
      dataIndex: 'status',
      width: 60,
      align: 'center',
      render: (_: any, record: Item) =>
        record.id ? (
          <Tag
            color={record.status === StatusEnum.Approved ? '#D6E6FF' : '#F4F6F9'}
            style={{ color: record.status === StatusEnum.Approved ? '#0D1A43' : '#838383', marginRight: 0 }}
          >
            {record.status === StatusEnum.Approved ? 'Approved' : 'Inactive'}
          </Tag>
        ) : null,
    },
    {
      title: 'Status',
      dataIndex: 'state',
      align: 'center',
      width: 60,
      render: (_: any, record: Item) =>
        record.id ? (
          <Tag
            color={record.active ? '#D6E6FF' : '#F4F6F9'}
            style={{ color: record.active ? '#0D1A43' : '#838383', marginRight: 0 }}
          >
            {record.active ? 'Active' : 'Inactive'}
          </Tag>
        ) : null,
    },
    {
      title: 'Format',
      dataIndex: 'format',
      align: 'center',
      width: 80,
      render: (_: any, record: any) => record.format && formatNames[record.format],
    },
    {
      title: 'Action',
      dataIndex: 'operation',
      width: 60,
      render: (_: any, record: Item) =>
        record.id ? (
          <Button onClick={() => unlink(record.id)} type="text" danger>
            <DeleteOutlined />
          </Button>
        ) : null,
    },
  ]

  return (
    <Spin spinning={loading}>
      <FormItem
        label="Link Creative"
        tooltip="Select an existing creative to be added to this campaign, or create a new one."
        style={{ width: '100%' }}
      >
        <Flex>
          <CreativeSelectorFiltered
            placeholder="Select Creative"
            key={selectorKey}
            advertiserId={entity.advertiserId}
            excludeIds={entity.creativeIds || []}
            onChange={(val?: number) => {
              void addCreative(val)
              setSelectorKey((k) => k + 1)
            }}
          />
          <Button type="primary" onClick={() => handleCreateOrEditCreative()} ghost style={{ marginLeft: 24 }}>
            <PlusOutlined />
            New Creative
          </Button>
        </Flex>
      </FormItem>

      {entity.creativeIds && entity.creativeIds.length > 0 && (
        <Row>
          <Col span="4"></Col>
          <Col span="16">
            <Table className="card-table" rowKey="id" dataSource={visibleListData} columns={columns} pagination={false} />
          </Col>
        </Row>
      )}
    </Spin>
  )
}

export default CreativesCard
